import React, { useEffect, useState, useCallback, useRef } from 'react';
import Select from 'react-select';

/** react-select shows duplicate rows if the same value appears twice — dedupe by option value. */
function dedupeSelectOptions(options) {
  const seen = new Set();
  return options.filter((opt) => {
    const valueKey = opt.value == null ? `__null__:${opt.label}` : String(opt.value);
    if (seen.has(valueKey)) return false;
    seen.add(valueKey);
    return true;
  });
}

const DynamicSelect = ({ props }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [menuPlacement, setMenuPlacement] = useState('auto');
  const [maxMenuHeight, setMaxMenuHeight] = useState(300);
  const selectRef = useRef(null);
  const selectId = `${props.hiddenInputId}_select`;
  const merged = props.resetOption
    ? [{ label: props.resetOption, value: null }, ...props.options]
    : props.options;
  const options = dedupeSelectOptions(merged);

  const calculateMenuPlacement = useCallback(() => {
    if (!selectRef.current) return;

    const rect = selectRef.current.getBoundingClientRect();
    const margin = 20; // breathing room from the viewport edge
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const desiredHeight = Math.min(300, 45 * options.length);

    // Open upwards only when there is materially more room above than below.
    const placeAbove = spaceBelow < desiredHeight && spaceAbove > spaceBelow;
    const availableSpace = placeAbove ? spaceAbove : spaceBelow;

    setMenuPlacement(placeAbove ? 'top' : 'bottom');
    // Cap the menu to the available space (min 150px) so it scrolls internally
    // instead of being clipped by the viewport / parent container.
    setMaxMenuHeight(Math.max(150, Math.min(desiredHeight, availableSpace)));
  }, [options.length]);

  useEffect(() => {
    if (props.value) {
      const valuesArray = props.isMulti
      ? props.value.split(',').map(val => val.toString())
      : [props.value.toString()];

      const selectedValues = options.filter(option =>
        valuesArray.includes(option.value?.toString())
      );

      handleChange(props.isMulti ? selectedValues : selectedValues[0]);
    } else if (props.resetOption) {
      handleChange(options[0]);
    }
  }, []);

  const handleChange = (selectedOption) => {
    setSelectedOption(selectedOption);

    if (props.onChange){
      props.onChange({ target: { value: selectedOption} });
    }

    if (props.hiddenInputId) {
      const hiddenInput = document.getElementById(props.hiddenInputId);
      if (hiddenInput) {
        if (props.isMulti) {
          if (selectedOption.length > 0) {
            hiddenInput.value = selectedOption
              ? selectedOption.filter(option => !!option.value).map((option) => option.value).join(',')
              : '';
          } else {
            hiddenInput.value = '';
          }
        } else {
          const raw = selectedOption?.value;
          hiddenInput.value =
            raw == null || raw === '' ? '' : String(raw);
        }

        // Emit a change event for Stimulus to pick up
        hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    
    // This is for the vehicles view to update the vehicle type field
    if (props.onCarSelected) {
      onCarSelected(selectedOption?.value || null);
    }

    // This is for the Book trip page to inject the Location and Destination to the other dropdowns
    if (props.setLocationDestination) {
      setLocationDestination(selectedOption?.value || null);
    }
  };

  const focusAccent = props.blueFocus ? '#2563eb' : '#6b7280';

  const customStyles = {
    control: (baseStyles, state) => ({
      ...baseStyles,
      border: `1px solid ${state.isFocused ? focusAccent : '#d1d5db'}`,
      boxShadow: 'none',
      backgroundColor: state.isDisabled ? '#f3f4f6' : (props.backgroundColor || 'white'),
      color: '#374151',
      borderRadius: '0.375rem',
      minHeight: '48px',
      fontSize: '14px',
      lineHeight: '1.25rem',
      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
      boxSizing: 'border-box',
      width: 'full',
      padding: '0.275rem 0.25rem 0.275rem 0.5rem',
      display: 'flex',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      opacity: state.isDisabled ? 0.6 : 1,
    }),
    menu: (baseStyles) => ({
      ...baseStyles,
      zIndex: 1000,
      // Keep the menu tight against the control instead of the default 8px gap.
      marginTop: '2px',
      marginBottom: '2px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    }),
    // When the menu is portalled to the body it needs a high z-index to sit
    // above everything else on the page.
    menuPortal: (baseStyles) => ({
      ...baseStyles,
      zIndex: 9999,
    }),
    menuList: (baseStyles) => ({
      ...baseStyles,
      maxHeight: `${maxMenuHeight}px`,
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        width: '8px'
      },
      '&::-webkit-scrollbar-track': {
        background: '#f1f1f1'
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#888',
        borderRadius: '4px'
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: '#555'
      },
    }),
    option: (baseStyles, state) => ({
      ...baseStyles,
      color: state.isSelected ? '#FFFFFF' : '#716C6E',
      backgroundColor: state.isDisabled
        ? '#e5e7eb'
        : state.isSelected
        ? '#4299E1'
        : state.isFocused
        ? '#f5f6f8'
        : 'white',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      opacity: state.isDisabled ? 0.65 : 1,
      pointerEvents: state.isDisabled ? 'auto' : 'auto', // allow hover/cursor for disabled
      // On hover, only apply if not disabled
      ...(state.isDisabled
        ? {}
        : {
            '&:hover': {
              backgroundColor: '#ede9fe',
              color: '#716C6E',
              cursor: 'pointer'
            },
          }),
    }),
    singleValue: (baseStyles) => ({
      ...baseStyles,
      color: '#374151',
      '&:hover': {
        color: '#374151',
        backgroundColor: 'transparent',
      },
    }),
    indicatorSeparator: (baseStyles) => ({
      ...baseStyles,
      backgroundColor: props.backgroundColor || '#FFFFFF'
    })
  };

  return (
    <div ref={selectRef}>
      <Select
        id={selectId}
        key={selectId}
        options={options}
        isMulti={props.isMulti}
        isDisabled={props.disabled}
        onChange={handleChange}
        value={selectedOption}
        placeholder={props.placeholder}
        isLoading={props.isLoading}
        menuPlacement={menuPlacement}
        maxMenuHeight={maxMenuHeight}
        menuPosition={props.menuPortal ? 'fixed' : 'absolute'}
        menuPortalTarget={props.menuPortal && typeof document !== 'undefined' ? document.body : undefined}
        menuShouldScrollIntoView={false}
        onMenuOpen={calculateMenuPlacement}
        styles={customStyles}
      />
    </div>
  );
};

export default DynamicSelect;